import type { QueryClient } from '@tanstack/react-query';
import type { PollRead } from '@models/api/poll';
import { writePollIntoCaches } from './writePollIntoCaches';

/**
 * The order of writes to one poll's block (TECH-197 § Edge cases "Races", "Cache").
 * `writePollIntoCaches` always overwrites, so what may write, and when, is decided here:
 *
 * - every answer (and FE-1c's end and reopen) takes a ticket when it starts; only the newest
 *   ticket's result is shown, so a stale response never overwrites a later tap;
 * - the block the server last confirmed is kept while writes are in flight: a failed newest write
 *   goes back to it (not to a guess an earlier tap made);
 * - a re-read writes only if no write started after it went out and none is in flight, so a late
 *   re-read never overwrites a newer vote, end or reopen.
 *
 * Module state, as one poll can show on two surfaces at once (the feed behind the Post sheet and
 * the sheet itself); `resetPollWriteOrderForTests` clears it.
 */
interface PollWriteState {
  latest: number;
  pending: number;
  /** What the server last said; null until a write starts. */
  confirmed: PollRead | null;
  /** What was last written through here (a guess or the server's block). */
  shown: PollRead | null;
}

/**
 * One mutation scope per poll for every write to its block (answer, end, reopen): they go out one
 * at a time, in order, so the last one back is the newest the server holds (`settlePollWrite`).
 */
export const pollWriteScopeId = (questionId: string) => `poll-write:${questionId}`;

let counter = 0;
const states = new Map<string, PollWriteState>();

const stateOf = (questionId: string): PollWriteState => {
  let state = states.get(questionId);
  if (!state) {
    state = { latest: 0, pending: 0, confirmed: null, shown: null };
    states.set(questionId, state);
  }
  return state;
};

export const resetPollWriteOrderForTests = (): void => {
  states.clear();
  counter = 0;
};

/** A write starts; `current` is the block on screen now (the server's, when nothing is in flight). */
export const beginPollWrite = (questionId: string, current: PollRead): number => {
  const state = stateOf(questionId);
  if (state.pending === 0) state.confirmed = current;
  counter += 1;
  state.latest = counter;
  state.pending += 1;
  return counter;
};

export const isLatestPollWrite = (questionId: string, ticket: number): boolean =>
  stateOf(questionId).latest === ticket;

/**
 * A write settles: `poll` is the server's block, or null when it failed. Says whether it was the
 * newest write (only then is its outcome shown) and what the server last confirmed.
 */
export const settlePollWrite = (
  questionId: string,
  ticket: number,
  poll: PollRead | null,
): { latest: boolean; confirmed: PollRead | null } => {
  const state = stateOf(questionId);
  state.pending = Math.max(0, state.pending - 1);
  // Writes go out one at a time (`pollWriteScopeId`), so the last one back is the newest
  // the server holds.
  if (poll) state.confirmed = poll;
  return { latest: state.latest === ticket, confirmed: state.confirmed };
};

/** Writes `poll` into every cached copy and records it as shown. */
export const showPoll = (queryClient: QueryClient, questionId: string, poll: PollRead): void => {
  stateOf(questionId).shown = poll;
  writePollIntoCaches(queryClient, questionId, poll);
};

/** A mark no write ticket ever equals (tickets start at 1). */
const NEVER_LATEST = -1;

/**
 * Taken when a read of the poll goes out. A read sent while a write is in flight may have been
 * answered before that write reached the server, so its mark never matches: it can only put the
 * block shown back, never its own copy, even when the write settles before it lands.
 */
export const pollReadMark = (questionId: string): number => {
  const state = stateOf(questionId);
  return state.pending > 0 ? NEVER_LATEST : state.latest;
};

/**
 * What a read that went out at `mark` may write: its own block, when nothing wrote since and
 * nothing is in flight; otherwise the block shown now, so the read's stale copy is put right.
 */
export const pollForRead = (questionId: string, mark: number, read: PollRead): PollRead => {
  const state = stateOf(questionId);
  if (state.pending === 0 && state.latest === mark) return read;
  return state.shown ?? read;
};
